import { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { API } from "../utils/constants";

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState(null);

  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      setNotificationError(null);

      const { retryOperation } = await import('../utils/networkResilience');

      const response = await retryOperation(async () => {
        return axios.get(`${API}/notifications`);
      }, {
        operationName: 'fetch_notifications',
        maxRetries: 2
      });

      setNotifications(response.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);

      const { parseBackendError, getErrorMessage } = await import('../utils/errorHandling');
      const parsedError = parseBackendError(error, { operation: 'fetch_notifications' });
      const errorMessage = getErrorMessage(parsedError.type, parsedError.context);
      setNotificationError(errorMessage.message);

      // Fallback to mock notifications if API fails
      setNotifications([
        {
          id: 1,
          title: "Asset Assignment Updated",
          message: "Asset #ASSET-001 has been assigned to Building A.",
          type: "info",
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          read: false
        },
        {
          id: 2,
          title: "New Property Added",
          message: "Union Park Alberton has been added to the system.",
          type: "success",
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          read: false
        },
        {
          id: 3,
          title: "Maintenance Required",
          message: "Equipment inspection overdue for Asset #ASSET-005.",
          type: "warning",
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          read: true
        }
      ]);
    } finally {
      setNotificationLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const { retryOperation } = await import('../utils/networkResilience');

      await retryOperation(async () => {
        return axios.patch(`${API}/notifications/${notificationId}/read`);
      }, {
        operationName: 'mark_notification_read',
        maxRetries: 2
      });

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const clearAll = async () => {
    try {
      const { retryOperation } = await import('../utils/networkResilience');

      await retryOperation(async () => {
        return axios.delete(`${API}/notifications/all`);
      }, {
        operationName: 'clear_notifications',
        maxRetries: 2
      });

      setNotifications([]);
      toast.success("All notifications cleared");
    } catch (error) {
      console.error("Error clearing notifications:", error);
      setNotifications([]);
    }
  };

  return {
    notifications,
    notificationLoading,
    notificationError,
    fetchNotifications,
    markAsRead,
    clearAll,
  };
};

export default useNotifications;
